import { useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X } from "lucide-react";

/**
 * BottomSheet — modal panel that slides in from the bottom.
 *
 * Design: mobile-first, safe-area aware, drag-to-dismiss on the handle.
 * Follows Apple / Material bottom sheet patterns: rounded top corners,
 * dim backdrop, spring animation, focus trap via `role=dialog`.
 *
 * Props:
 *   open         — boolean
 *   onClose      — () => void
 *   title        — string (rendered in the sticky header)
 *   subtitle     — string (optional, small text under title)
 *   accent       — hex/color used for the icon pill and title dot
 *   icon         — lucide Icon component
 *   children     — sheet body
 *   testId       — data-testid root
 */
const BottomSheet = ({ open, onClose, title, subtitle, accent = "#0EA5E9", icon: Icon, children, testId, z = 100 }) => {
  // Lock body scroll while open — restore on close.
  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = prev; };
  }, [open]);

  // ESC to close.
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === "Escape") onClose?.(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          className="fixed inset-0 flex items-end justify-center"
          style={{ zIndex: z }}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.2 }}
          data-testid={testId}
        >
          {/* Backdrop */}
          <motion.div
            className="absolute inset-0 bg-black/45 backdrop-blur-sm"
            onClick={onClose}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          />

          {/* Sheet */}
          <motion.div
            role="dialog"
            aria-modal="true"
            className="relative w-full sm:max-w-2xl bg-white shadow-2xl overflow-hidden"
            style={{
              borderTopLeftRadius: 28,
              borderTopRightRadius: 28,
              maxHeight: "calc(var(--frame-h, 100vh) * 0.92)",
              paddingBottom: "env(safe-area-inset-bottom, 0px)",
            }}
            initial={{ y: "100%" }}
            animate={{ y: 0 }}
            exit={{ y: "100%" }}
            transition={{ type: "spring", damping: 32, stiffness: 320, mass: 0.8 }}
          >
            {/* Grabber (solo estetico — la chiusura avviene con la X) */}
            <div className="flex justify-center pt-3 pb-1">
              <span className="w-10 h-1.5 rounded-full bg-zinc-200" />
            </div>

            {/* Header */}
            <div className="px-5 pb-3 flex items-center gap-3">
              {Icon && (
                <div
                  className="w-11 h-11 rounded-2xl flex items-center justify-center shrink-0"
                  style={{ background: `${accent}15` }}
                >
                  <Icon className="w-5 h-5" style={{ color: accent }} strokeWidth={2} />
                </div>
              )}
              <div className="flex-1 min-w-0">
                {title && (
                  <p className="text-[17px] font-black text-zinc-900 tracking-tight leading-tight truncate">
                    {title}
                  </p>
                )}
                {subtitle && (
                  <p className="text-[12px] text-zinc-500 font-medium leading-tight truncate">
                    {subtitle}
                  </p>
                )}
              </div>
              <button
                onClick={onClose}
                className="w-9 h-9 rounded-full bg-zinc-100 hover:bg-zinc-200 flex items-center justify-center transition-colors shrink-0"
                aria-label="Chiudi"
                data-testid="bottom-sheet-close"
              >
                <X className="w-4 h-4 text-zinc-700" strokeWidth={2.4} />
              </button>
            </div>

            {/* Body — scrollable */}
            <div
              className="overflow-y-auto px-5 pb-6"
              style={{ maxHeight: "calc(var(--frame-h, 100vh) * 0.92 - 88px)", WebkitOverflowScrolling: "touch", overscrollBehavior: "contain" }}
            >
              {children}
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default BottomSheet;
